import { BookingStatus } from "@/types";

/**
 * Booking Status State Machine
 *
 * Enforces valid status transitions and provides user-friendly UI metadata.
 *
 * State Flow:
 * ┌──────────────────┐
 * │ pending-approval │
 * └────────┬─────────┘
 *          │
 *    ┌─────┴─────┐
 *    ▼           ▼
 * ┌──────┐   ┌────────┐
 * │denied│   │approved│
 * └──────┘   └───┬────┘
 *   (end)        │
 *                ▼
 *          ┌─────────┐
 *          │confirmed│◄────────────────┐
 *          └────┬────┘                 │
 *               │                      │
 *               ▼                      │
 *        ┌───────────┐                 │
 *        │in-progress│                 │
 *        └─────┬─────┘                 │
 *              │                       │
 *        ┌─────┴─────┐                 │
 *        ▼           ▼                 │
 *  ┌─────────┐  ┌─────────┐            │
 *  │completed│  │delivered│            │
 *  └─────────┘  └─────────┘            │
 *     (end)        (end)               │
 *                                      │
 * [cancelled] ◄────────────────────────┘
 *   (can cancel from: pending-approval, approved, confirmed)
 */

/**
 * Status metadata for UI display
 */
export interface StatusMetadata {
  label: string;
  description: string;
  color: "warning" | "success" | "danger" | "info" | "default" | "primary";
  icon: string; // Icon name suggestion for FI icons
  isTerminal: boolean;
  allowsCancel: boolean;
  progressPercent: number; // 0-100 for progress indicator
}

/**
 * Transition metadata for UI display
 */
export interface TransitionMetadata {
  label: string;
  description: string;
  confirmMessage?: string;
  requiresReason?: boolean;
  buttonVariant: "primary" | "success" | "danger" | "warning" | "outline";
  icon: string;
}

/**
 * Valid transitions from each status
 */
const VALID_TRANSITIONS: Record<BookingStatus, BookingStatus[]> = {
  "pending-approval": ["approved", "denied", "cancelled"],
  approved: ["confirmed", "cancelled"],
  denied: [], // Terminal state
  confirmed: ["in-progress", "cancelled"],
  "in-progress": ["completed", "delivered", "cancelled"],
  completed: ["delivered"], // Can still deliver after completion
  cancelled: [], // Terminal state
  delivered: [], // Terminal state
};

/**
 * Status metadata for user-friendly display
 */
export const STATUS_METADATA: Record<BookingStatus, StatusMetadata> = {
  "pending-approval": {
    label: "Pending Approval",
    description: "Waiting for admin to review and approve this booking",
    color: "warning",
    icon: "FiClock",
    isTerminal: false,
    allowsCancel: true,
    progressPercent: 10,
  },
  approved: {
    label: "Approved",
    description: "Booking approved, waiting for customer confirmation",
    color: "info",
    icon: "FiCheckCircle",
    isTerminal: false,
    allowsCancel: true,
    progressPercent: 30,
  },
  denied: {
    label: "Denied",
    description: "Booking was denied by admin",
    color: "danger",
    icon: "FiXCircle",
    isTerminal: true,
    allowsCancel: false,
    progressPercent: 0,
  },
  confirmed: {
    label: "Confirmed",
    description: "Booking is confirmed and scheduled",
    color: "success",
    icon: "FiCalendar",
    isTerminal: false,
    allowsCancel: true,
    progressPercent: 50,
  },
  "in-progress": {
    label: "In Progress",
    description: "Service is currently being performed",
    color: "primary",
    icon: "FiPlay",
    isTerminal: false,
    allowsCancel: true,
    progressPercent: 75,
  },
  completed: {
    label: "Completed",
    description: "Service has been completed, awaiting delivery",
    color: "success",
    icon: "FiCheck",
    isTerminal: false,
    allowsCancel: false,
    progressPercent: 90,
  },
  cancelled: {
    label: "Cancelled",
    description: "Booking was cancelled",
    color: "danger",
    icon: "FiSlash",
    isTerminal: true,
    allowsCancel: false,
    progressPercent: 0,
  },
  delivered: {
    label: "Delivered",
    description: "Deliverables have been sent to the customer",
    color: "success",
    icon: "FiSend",
    isTerminal: true,
    allowsCancel: false,
    progressPercent: 100,
  },
};

/**
 * Transition metadata for each possible transition
 */
export const TRANSITION_METADATA: Record<string, TransitionMetadata> = {
  // From pending-approval
  "pending-approval→approved": {
    label: "Approve",
    description: "Approve this booking request",
    confirmMessage: "Approve this booking? The customer will be notified.",
    buttonVariant: "success",
    icon: "FiCheckCircle",
  },
  "pending-approval→denied": {
    label: "Deny",
    description: "Deny this booking request",
    confirmMessage: "Deny this booking? Please provide a reason for the customer.",
    requiresReason: true,
    buttonVariant: "danger",
    icon: "FiXCircle",
  },
  "pending-approval→cancelled": {
    label: "Cancel",
    description: "Cancel this booking request",
    confirmMessage: "Cancel this booking? This action cannot be undone.",
    requiresReason: true,
    buttonVariant: "outline",
    icon: "FiSlash",
  },

  // From approved
  "approved→confirmed": {
    label: "Confirm",
    description: "Confirm this booking for the scheduled date",
    confirmMessage: "Confirm this booking? The customer will receive a confirmation.",
    buttonVariant: "success",
    icon: "FiCalendar",
  },
  "approved→cancelled": {
    label: "Cancel",
    description: "Cancel this approved booking",
    confirmMessage: "Cancel this booking? The customer will be notified.",
    requiresReason: true,
    buttonVariant: "outline",
    icon: "FiSlash",
  },

  // From confirmed
  "confirmed→in-progress": {
    label: "Start Service",
    description: "Mark service as started",
    confirmMessage: "Start the service? The status will change to In Progress.",
    buttonVariant: "primary",
    icon: "FiPlay",
  },
  "confirmed→cancelled": {
    label: "Cancel",
    description: "Cancel this confirmed booking",
    confirmMessage:
      "Cancel this confirmed booking? The customer will be notified and may need a refund.",
    requiresReason: true,
    buttonVariant: "danger",
    icon: "FiSlash",
  },

  // From in-progress
  "in-progress→completed": {
    label: "Mark Complete",
    description: "Mark the service as completed",
    confirmMessage: "Mark as completed? You can still send deliverables after this.",
    buttonVariant: "success",
    icon: "FiCheck",
  },
  "in-progress→delivered": {
    label: "Mark Delivered",
    description: "Skip to delivered status if deliverables were sent during service",
    confirmMessage: "Mark as delivered? This means the service and deliverables are complete.",
    buttonVariant: "success",
    icon: "FiSend",
  },
  "in-progress→cancelled": {
    label: "Cancel",
    description: "Cancel this in-progress booking",
    confirmMessage: "Cancel this booking mid-service? This may require partial refund handling.",
    requiresReason: true,
    buttonVariant: "danger",
    icon: "FiSlash",
  },

  // From completed
  "completed→delivered": {
    label: "Mark Delivered",
    description: "Confirm deliverables have been sent to customer",
    confirmMessage: "Confirm deliverables have been sent?",
    buttonVariant: "success",
    icon: "FiSend",
  },
};

/**
 * Check if a status transition is valid
 */
export function canTransitionTo(currentStatus: BookingStatus, newStatus: BookingStatus): boolean {
  return VALID_TRANSITIONS[currentStatus]?.includes(newStatus) ?? false;
}

/**
 * Get all valid transitions from a given status
 */
export function getValidTransitions(currentStatus: BookingStatus): BookingStatus[] {
  return VALID_TRANSITIONS[currentStatus] || [];
}

/**
 * Get transition metadata for a specific transition
 */
export function getTransitionMetadata(
  fromStatus: BookingStatus,
  toStatus: BookingStatus
): TransitionMetadata | null {
  const key = `${fromStatus}→${toStatus}`;
  return TRANSITION_METADATA[key] || null;
}

/**
 * Get status metadata
 */
export function getStatusMetadata(status: BookingStatus): StatusMetadata {
  return STATUS_METADATA[status];
}

/**
 * Validate status transition and throw error if invalid
 */
export function validateStatusTransition(
  currentStatus: BookingStatus,
  newStatus: BookingStatus
): void {
  if (currentStatus === newStatus) {
    throw new StatusTransitionError(
      `Booking is already in "${getStatusMetadata(currentStatus).label}" status`,
      currentStatus,
      newStatus
    );
  }

  if (!canTransitionTo(currentStatus, newStatus)) {
    const currentMeta = getStatusMetadata(currentStatus);
    const newMeta = getStatusMetadata(newStatus);
    const validOptions = getValidTransitions(currentStatus)
      .map((s) => getStatusMetadata(s).label)
      .join(", ");

    throw new StatusTransitionError(
      `Cannot transition from "${currentMeta.label}" to "${newMeta.label}". ` +
        (validOptions
          ? `Valid options are: ${validOptions}`
          : `${currentMeta.label} is a terminal status and cannot be changed.`),
      currentStatus,
      newStatus
    );
  }
}

/**
 * Custom error class for invalid status transitions
 */
export class StatusTransitionError extends Error {
  public readonly currentStatus: BookingStatus;
  public readonly attemptedStatus: BookingStatus;

  constructor(message: string, currentStatus: BookingStatus, attemptedStatus: BookingStatus) {
    super(message);
    this.name = "StatusTransitionError";
    this.currentStatus = currentStatus;
    this.attemptedStatus = attemptedStatus;
  }
}

/**
 * Get a user-friendly status progression array for displaying workflow
 */
export function getStatusProgression(): { status: BookingStatus; order: number }[] {
  return [
    { status: "pending-approval", order: 1 },
    { status: "approved", order: 2 },
    { status: "confirmed", order: 3 },
    { status: "in-progress", order: 4 },
    { status: "completed", order: 5 },
    { status: "delivered", order: 6 },
  ];
}

/**
 * Get status color for Badge/UI components
 */
export function getStatusColor(status: BookingStatus): StatusMetadata["color"] {
  return STATUS_METADATA[status]?.color || "default";
}

/**
 * Check if a status is terminal (no further transitions possible)
 */
export function isTerminalStatus(status: BookingStatus): boolean {
  return STATUS_METADATA[status]?.isTerminal || false;
}

/**
 * Check if a booking can be cancelled from its current status
 */
export function canBeCancelled(status: BookingStatus): boolean {
  return STATUS_METADATA[status]?.allowsCancel || false;
}

/**
 * Get the next logical status in the workflow (for auto-suggest)
 */
export function getNextLogicalStatus(currentStatus: BookingStatus): BookingStatus | null {
  const progression = getStatusProgression();
  const currentIndex = progression.findIndex((p) => p.status === currentStatus);

  if (currentIndex === -1 || currentIndex === progression.length - 1) {
    return null;
  }

  const nextStatus = progression[currentIndex + 1].status;

  // Verify this is a valid transition
  if (canTransitionTo(currentStatus, nextStatus)) {
    return nextStatus;
  }

  return null;
}
